import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo, CLASSES_JSONLD } from "@/components/Seo";
import { FadeUp } from "@/components/anim";
import { api } from "@/lib/api";
import { STYLE_META, fmtTime, FALLBACK_IMAGE } from "@/lib/site";

const DETAILS = {
  "Early Years": {
    heading: "Happy Feet — first ballet & early tap",
    body: "Our littlest dancers (from age 2) take their very first ballet steps — posture, positions and musicality made magical — alongside the early stages of tap rhythm. A gentle, joyful start to proper training.",
    img: "https://images.unsplash.com/photo-1595348514401-eca68a3bea33?w=900&q=80",
  },
  Ballet: {
    heading: "Classical ballet, ISTD syllabus",
    body: "From Primary through the grades to Intermediate — posture, poise, strength and musicality, built step by careful step. Exams are there when dancers are ready. And with a 100% pass record, they always are.",
    img: "https://images.unsplash.com/photo-1495791185843-c73f2269f669?w=900&q=80",
  },
  Tap: {
    heading: "Rhythm you can hear",
    body: "Tap is music you make with your feet. Following the ISTD syllabus, dancers build rhythm, speed and coordination — from first shuffles to advanced time steps. Rhythmic, joyful, seriously addictive.",
    img: "https://images.unsplash.com/photo-1533106958148-daaeab8b83fe?w=900&q=80",
  },
  Modern: {
    heading: "Theatre dance with punch",
    body: "ISTD Modern is the style you see in West End ensembles — kicks, leaps, turns and performance quality. Classes run from Primary right through to Intermediate, with exams at every grade.",
    img: "https://images.unsplash.com/photo-1518834107812-67b0b7c58434?w=900&q=80",
  },
  "Musical Theatre": {
    heading: "Sing. Dance. Act. Shine.",
    body: "Triple-threat training across Junior, Inter and Advanced groups. Numbers from real shows, characterisation, vocals, and the stagecraft to light up any audience. Kerry trained at Laine — this is the real deal.",
    img: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=900&q=80",
  },
  "Street Dance": {
    heading: "Commercial & hip hop",
    body: "Commercial and hip hop styles danced to the music kids actually love. Junior, Intermediate and Senior groups mean everyone trains at their own level — sharp, current choreography and plenty of energy.",
    img: "https://images.unsplash.com/photo-1546427660-eb346c344ba5?w=900&q=80",
  },
  Contemporary: {
    heading: "Movement with meaning",
    body: "Fluid, grounded contemporary technique that builds strength, expression and artistry — woven into our Senior Modern / Contemporary class on Monday evenings. The perfect partner to ballet for anyone eyeing vocational training.",
    img: "https://images.unsplash.com/photo-1531301147855-9f3e913bfe58?w=900&q=80",
  },
  Singing: {
    heading: "Find your voice",
    body: "Vocal technique, breathing, harmonies and the confidence to sing out — taught with the same warmth as everything else at Kerry's. Wednesday singing lessons are joining the timetable (times TBC) — get in touch to register interest.",
    img: "https://images.unsplash.com/photo-1453738773917-9c3eff1db985?w=900&q=80",
  },
  "Audition Prep": {
    heading: "Ready for the big room",
    body: "For seniors applying to vocational colleges or professional jobs: solo coaching, technique polish and how to walk into an audition ready. Kerry has been through those doors herself — and coached plenty of dancers through them since. Mondays at 8.30pm.",
    img: "https://images.unsplash.com/photo-1535525153412-5a42439a210d?w=900&q=80",
  },
  "Body Conditioning": {
    heading: "Stronger, bendier, unstoppable",
    body: "Strength, flexibility and stamina work built for dancers — the training that makes every other class easier and keeps growing bodies safe. Fridays at 8pm at the Pool dance studio.",
    img: "https://images.unsplash.com/photo-1524863479829-916d8e77f114?w=900&q=80",
  },
};

export default function Classes() {
  const [timetable, setTimetable] = useState([]);
  useEffect(() => {
    api
      .get("/timetable")
      .then((res) => setTimetable(res.data))
      .catch(() => {});
  }, []);

  const classesFor = (style) =>
    timetable.filter((c) => c.style === style || c.name.toLowerCase().includes(style.toLowerCase()));

  return (
    <>
      <Seo
        title="Dance Classes"
        description={"ISTD Ballet, Tap and Modern plus Musical Theatre, Street Dance, Contemporary and early years classes in Whitstable for ages 2–18, plus audition prep and body conditioning. No auditions to join — SEND and neurodiverse dancers warmly welcome."}
        path="/classes"
        jsonLd={CLASSES_JSONLD}
      />
      <section className="grain border-b-[0.5px] border-foreground/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-24">
          <FadeUp className="max-w-3xl">
            <Badge variant="outline" className="rounded-none border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun))] font-bold uppercase tracking-[0.15em] text-[11px] px-4 py-1.5 mb-6">
              ISTD syllabus &middot; 100% exam pass record
            </Badge>
            <h1 className="font-display uppercase text-6xl sm:text-8xl lg:text-[6.5rem] leading-[0.88]">
              The classes
            </h1>
            <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Ten class families, ages 2 to 18, not an audition in sight. Whether your child dreams of the West
              End or just wants the best hour of their week &mdash; there&rsquo;s a spot here with their name on it. SEND and neurodiverse dancers are warmly welcome &mdash; classes adapt to the child, not the other way round.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px] space-y-[120px]" data-testid="classes-list-section">
        {Object.entries(STYLE_META).map(([name, meta], i) => {
          const detail = DETAILS[name] || {};
          const sessions = classesFor(name);
          const flip = i % 2 === 1;
          return (
            <FadeUp key={name}>
              <article className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start" data-testid="classes-style-card">
                <div className={`lg:col-span-5 ${flip ? "lg:order-2 lg:mt-14" : ""}`}>
                  <img
                    src={detail.img}
                    alt={`${name} class at Kerry's`}
                    loading="lazy"
                    className="w-full h-[320px] sm:h-[400px] object-cover border-[0.5px] border-foreground"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_IMAGE;
                    }}
                  />
                </div>
                <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
                  <p className="font-display text-2xl text-muted-foreground">0{i + 1}</p>
                  <h2 className="font-display uppercase text-5xl sm:text-7xl leading-[0.9] mt-2">{name}</h2>
                  <p className="text-sm font-bold uppercase tracking-[0.2em] text-[hsl(var(--kerrys-sea))] mt-4">{detail.heading}</p>
                  <p className="mt-5 text-muted-foreground leading-relaxed max-w-prose">{detail.body}</p>
                  <div className="flex flex-wrap gap-2 mt-6">
                    <Badge variant="outline" className="rounded-none border-[0.5px] border-foreground font-bold text-xs uppercase tracking-[0.1em]">
                      Ages {meta.ages}
                    </Badge>
                    {["Ballet", "Tap", "Modern"].includes(name) && (
                      <Badge variant="outline" className="rounded-none border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun))] font-bold text-xs uppercase tracking-[0.1em]">
                        ISTD exams
                      </Badge>
                    )}
                    <Badge variant="outline" className="rounded-none border-[0.5px] border-foreground font-bold text-xs uppercase tracking-[0.1em]">
                      No experience needed
                    </Badge>
                  </div>

                  <div className="mt-8 border-[0.5px] border-foreground bg-secondary grain p-6">
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-foreground/70 mb-4">On the timetable</p>
                    {sessions.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Times coming soon &mdash; check the full timetable.</p>
                    ) : (
                      <ul className="divide-y-[0.5px] divide-foreground/40">
                        {sessions.map((s) => (
                          <li key={s.id} className="flex items-center justify-between gap-3 py-2.5 text-sm" data-testid="classes-session-row">
                            <span className="font-bold truncate">{s.name}</span>
                            <span className="text-muted-foreground shrink-0 font-display text-lg">
                              {s.day.slice(0, 3)} {fmtTime(s.start_time)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="mt-7 flex flex-wrap items-center gap-5">
                    <Button
                      asChild
                      className="rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-xs h-11 px-6"
                      data-testid="classes-style-card-view-button"
                    >
                      <Link to="/book">
                        Book {name} <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                    <Link to="/timetable" className="text-xs font-bold uppercase tracking-[0.15em] text-[hsl(var(--kerrys-sea))] hover:text-foreground transition-colors">
                      Full timetable &rarr;
                    </Link>
                  </div>
                </div>
              </article>
            </FadeUp>
          );
        })}
      </section>
    </>
  );
}
